import * as _ from 'lodash';
import { ICenterBase, IContact, IListItem, IMicrositeRecipientForList, IMicrositeRecipientLinkedEntityList, IRemoteDonorRegistration } from '@nkr/models/index';
import { MicrositeEmailType } from '@nkr/enums/index';
import { IMicrositeDashRegistration } from '@nkr/store/index';
import { IMicrositeRecipientForListWithSelect } from './models';
import { ColumnApi, GridApi } from 'ag-grid-community';

interface ISearch {
    coachContactId: number;
    center: number,
    status: number,
    type: number,
    startDate: string,
    endDate: string,
    search: string,
    campaign: string
}

interface IState {
    list: IMicrositeRecipientForListWithSelect[],
    centerList: ICenterBase[],
    statusList: IListItem[],
    genderList: IListItem[],
    stateList: IListItem[],
    donorOriginList: IListItem[],
    emailTypeList: IListItem[],
    micrositeTypeList: IListItem[],
    dashRegistrationList: IMicrositeDashRegistration[],
    coordinatorList: IContact[],
    coachList: IContact[],
    eventTypeList: IListItem[],
    search: ISearch,
    selectAllRows: boolean,
    sendReminderEmail: {
        show: boolean,
        data: IMicrositeRecipientForList
    },
    dashRegistrationForRemotePatients: {
        show: boolean,
        data: IRemoteDonorRegistration,
        microsite: IMicrositeRecipientForListWithSelect,
        // patientName: string
    },
    linkedEntitiesList: {
        show: boolean,
        data: IMicrositeRecipientLinkedEntityList[]
    },
    resendApprovalEmail: {
        show: boolean,
        data: IMicrositeRecipientForList
    },
    downloadCardBatchFile: {
        show: boolean,
        data: {
            id: number,
            filename: string
        }
    },
    bulkDeactivate: {
        show: boolean,
        list: IMicrositeRecipientForList[]
    },
    bulkSendEmail: {
        show: boolean,
        emailType: MicrositeEmailType | number,
        list: IMicrositeRecipientForList[]
    },
    changeStatus: {
        show: boolean,
        missingData: boolean,
        data: IMicrositeRecipientForList
    },
    bulkChangeStatus: {
        show: boolean,
        list: IMicrositeRecipientForList[]
    },
    isLoading: boolean,
    gridApi: GridApi,
    columnApi: ColumnApi
}

const initialState: IState = {
    list: [],
    centerList: [],
    statusList: [],
    genderList: [],
    stateList: [],
    donorOriginList: [],
    emailTypeList: [],
    micrositeTypeList: [],
    dashRegistrationList: [],
    coordinatorList: [],
    coachList: [],
    eventTypeList: [],
    search: {
        coachContactId: -1,
        center: 0,
        status: -1,
        type: -1,
        startDate: '',
        endDate: '',
        search: '',
        campaign: ''
    },
    selectAllRows: false,
    dashRegistrationForRemotePatients: {
        show: false,
        data: null,
        microsite: null,
        // patientName: ''
    },
    sendReminderEmail: {
        show: false,
        data: null
    },
    linkedEntitiesList: {
        show: false,
        data: null
    },
    resendApprovalEmail: {
        show: false,
        data: null
    },
    downloadCardBatchFile: {
        show: false,
        data: {
            id: 0,
            filename: ''
        }
    },
    bulkDeactivate: {
        show: false,
        list: []
    },
    bulkSendEmail: {
        show: false,
        emailType: 0,
        list: []
    },
    changeStatus: {
        show: false,
        missingData: false,
        data: null,
    },
    bulkChangeStatus: {
        show: false,
        list: []
    },
    isLoading: true,
    gridApi: null,
    columnApi: null
};

interface ILookupData {
    centerList: ICenterBase[],
    statusList: IListItem[],
    genderList: IListItem[],
    stateList: IListItem[],
    donorOriginList: IListItem[],
    emailTypeList: IListItem[],
    micrositeTypeList: IListItem[],
    dashRegistrationList: IMicrositeDashRegistration[],
    coordinatorList: IContact[],
    coachList: IContact[],
    eventTypeList: IListItem[],
}

type TAction =
    | { type: 'setGrid', payload: { gridApi: GridApi, columnApi: ColumnApi } }
    | { type: 'setList', payload: IMicrositeRecipientForListWithSelect[] }
    | { type: 'setLookupData', payload: ILookupData }
    | { type: 'setSearch', payload: ISearch }
    | { type: 'setLoading', payload: boolean }
    | { type: 'selectAllRows', payload: boolean }
    | { type: 'sendReminderEmail', payload: { show: boolean, data: IMicrositeRecipientForList } }
    | { type: 'dashRegistrationForRemotePatients', payload: { show: boolean, data: IRemoteDonorRegistration, microsite: IMicrositeRecipientForListWithSelect } }
    | { type: 'linkedEntitiesList', payload: { show: boolean, data: IMicrositeRecipientLinkedEntityList[] } }
    | { type: 'resendApprovalEmail', payload: { show: boolean, data: IMicrositeRecipientForListWithSelect } }
    | { type: 'changeStatus', payload: { show: boolean, missingData: boolean, data: IMicrositeRecipientForList } }
    | { type: 'downloadCardBatchFile', payload: { show: boolean, data: { id: number; filename: string; } } }
    | { type: 'bulkDeactivate', payload: { show: boolean, list: IMicrositeRecipientForList[] } }
    | { type: 'bulkSendEmail', payload: { show: boolean, emailType: MicrositeEmailType, list: IMicrositeRecipientForList[] } }
    | { type: 'bulkChangeStatus', payload: { show: boolean, list: IMicrositeRecipientForList[] } }
    | { type: 'updateAssignedCoach', payload: { micrositeId: number, coachContactId: number } }
    | { type: 'afterGetData', payload: { list: IMicrositeRecipientForListWithSelect[], lookupData: ILookupData, search: ISearch } }
    ;

const reducer = (state: IState, action: TAction): IState => {
    switch (action.type) {
        case 'setGrid':
            return { ...state, ...action.payload };

        case 'setList':
            return { ...state, list: action.payload };

        case 'setLookupData':
            return { ...state, ...action.payload };

        case 'setSearch':
            return { ...state, search: action.payload };

        case 'setLoading':
            return { ...state, isLoading: action.payload };

        case 'selectAllRows':
            return { ...state, selectAllRows: action.payload };

        case 'sendReminderEmail':
            return { ...state, sendReminderEmail: action.payload };

        case 'dashRegistrationForRemotePatients':
            return { ...state, dashRegistrationForRemotePatients: action.payload };

        case 'linkedEntitiesList':
            return { ...state, linkedEntitiesList: action.payload };

        case 'resendApprovalEmail':
            return { ...state, resendApprovalEmail: action.payload };

        case 'changeStatus':
            return { ...state, changeStatus: action.payload };

        case 'downloadCardBatchFile':
            return { ...state, downloadCardBatchFile: action.payload };

        case 'bulkDeactivate':
            return { ...state, bulkDeactivate: action.payload };

        case 'bulkSendEmail':
            return { ...state, bulkSendEmail: action.payload };

        case 'bulkChangeStatus':
            return { ...state, bulkChangeStatus: action.payload };

        case 'updateAssignedCoach':
            let list = _.cloneDeep(state.list).map(x => {
                if (x.id == action.payload.micrositeId) {
                    x.coachContactId = action.payload.coachContactId;
                }

                return x;
            })

            return { ...state, list };

        case 'afterGetData':
            return { ...state, list: action.payload.list, search: action.payload.search, ...action.payload.lookupData, isLoading: false };

        default:
            return { ...state };
    }
}

export { initialState, reducer };